/**
 * DEN PERSONLIGE SIDEN (persontypene §3.1, migrering 1090).
 *
 * Frivillige får aldri innlogging. De får én fast lenke per festival og årgang, og siden bak den er
 * den samme lenkepersonene har (`/v1/share-links/:token`): de delte tingene som kort, og i tillegg
 * det som hører til personen i årgangen — vaktene, akkrediteringen, kontaktpersonen og «Mine
 * opplysninger». `personal` er null for en ren lenkeperson.
 */
import { z } from 'zod';

import { festivalKindSchema } from '../enums.js';

import { supplierPageSchema } from './suppliers.js';

/** Svarene på en vakt fra siden — samme modell som `PUT /v1/shifts/:id/response`. */
export const PERSONAL_SHIFT_ANSWERS = ['confirmed', 'declined'] as const;
export type PersonalShiftAnswer = (typeof PERSONAL_SHIFT_ANSWERS)[number];

export const personalPageShiftSchema = z
  .object({
    shiftId: z.uuid(),
    title: z.string().nullable(),
    /** Posten (`staffing_roles`). */
    post: z.string().nullable(),
    department: z.string().nullable(),
    /** Ressursen vakta står på (baren, porten), når den har en. */
    place: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    status: z.enum(['assigned', 'confirmed', 'declined']),
  })
  .meta({ id: 'PersonalPageShift' });
export type PersonalPageShift = z.infer<typeof personalPageShiftSchema>;

export const personalPageContactSchema = z
  .object({
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
  })
  .meta({ id: 'PersonalPageContact' });
export type PersonalPageContact = z.infer<typeof personalPageContactSchema>;

/** «Mine opplysninger» — organisasjonens `person_facts` om personen, slik hen selv retter dem. */
export const personalDetailsSchema = z
  .object({
    tshirtSize: z.string().nullable(),
    drivingLicence: z.string().nullable(),
    dateOfBirth: z.string().nullable(),
    food: z.string().nullable(),
    emergencyName: z.string().nullable(),
    emergencyPhone: z.string().nullable(),
    emergencyRelation: z.string().nullable(),
  })
  .meta({ id: 'PersonalDetails' });
export type PersonalDetails = z.infer<typeof personalDetailsSchema>;

const detailText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));

/** `PATCH /v1/share-links/:token/details`. Hvert felt kan utelates, eller tømmes med null. */
export const updatePersonalDetailsRequestSchema = z
  .object({
    tshirtSize: detailText(40).optional(),
    drivingLicence: detailText(120).optional(),
    dateOfBirth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .refine((value) => value >= '1900-01-01', { message: 'date_of_birth_too_early' })
      .nullable()
      .optional(),
    food: detailText(1500).optional(),
    emergencyName: detailText(120).optional(),
    emergencyPhone: detailText(40).optional(),
    emergencyRelation: detailText(60).optional(),
  })
  .strict()
  .meta({ id: 'UpdatePersonalDetailsRequest' });
export type UpdatePersonalDetailsRequest = z.infer<typeof updatePersonalDetailsRequestSchema>;

/** `PUT /v1/share-links/:token/shifts/:shiftId/response`. */
export const respondPersonalShiftRequestSchema = z
  .object({ status: z.enum(PERSONAL_SHIFT_ANSWERS) })
  .strict()
  .meta({ id: 'RespondPersonalShiftRequest' });
export type RespondPersonalShiftRequest = z.infer<typeof respondPersonalShiftRequestSchema>;

export const respondPersonalShiftResponseSchema = z
  .object({ shiftId: z.uuid(), status: z.enum(PERSONAL_SHIFT_ANSWERS) })
  .meta({ id: 'RespondPersonalShiftResponse' });
export type RespondPersonalShiftResponse = z.infer<typeof respondPersonalShiftResponseSchema>;

/**
 * Det personlige på siden. Seksjonene står i den rekkefølgen hurtiglenkene har: Vakter · Info ·
 * Dokumenter · Mine opplysninger · Kontakt. En annen agent legger til en seksjon som et nytt,
 * valgfritt felt her og en ny oppføring i `PERSONAL_PAGE_SECTIONS` på weben.
 */
export const personalPageSchema = z
  .object({
    festivalKind: festivalKindSchema.optional(),
    logoUrl: z.string().nullable(),
    startsOn: z.iso.date().nullable(),
    endsOn: z.iso.date().nullable(),
    /** Organisasjonens egne kontaktopplysninger, når ingen avdelingsleder er kontaktperson. */
    organizationPhone: z.string().nullable(),
    organizationEmail: z.string().nullable(),
    /** Om årgangen lar noen takke nei til en vakt (`editions.shift_decline_allowed`). */
    declineAllowed: z.boolean(),
    signup: z
      .object({
        status: z.enum(['pending', 'approved', 'rejected', 'withdrawn']),
        formName: z.string().nullable(),
      })
      .nullable(),
    shifts: z.array(personalPageShiftSchema),
    /** Hva som står på armbåndet. Null er ingen akkreditering ennå. */
    accreditation: z.object({ name: z.string(), zones: z.array(z.string()) }).nullable(),
    contact: personalPageContactSchema.nullable(),
    details: personalDetailsSchema,
    /** 1097 (D): firmaet og de ansatte, når personen er kontaktperson for en leverandør. */
    supplier: supplierPageSchema.nullable().optional(),
  })
  .meta({ id: 'PersonalPage' });
export type PersonalPage = z.infer<typeof personalPageSchema>;

/** De automatiske e-postene (§3.5). Én mal per type og årgang. */
export const VOLUNTEER_MESSAGE_KINDS = [
  'signup_received',
  'signup_approved',
  'signup_rejected',
  'shift_assigned',
  'shift_changed',
  'shift_reminder',
] as const;
export type VolunteerMessageKind = (typeof VOLUNTEER_MESSAGE_KINDS)[number];
export const volunteerMessageKindSchema = z
  .enum(VOLUNTEER_MESSAGE_KINDS)
  .meta({ id: 'VolunteerMessageKind' });

/** Standarden uten egen rad — samme tabell som `app.volunteer_template_enabled` (1090). */
export const VOLUNTEER_MESSAGE_DEFAULTS: Readonly<Record<VolunteerMessageKind, boolean>> = {
  signup_received: true,
  signup_approved: false,
  signup_rejected: false,
  shift_assigned: false,
  shift_changed: false,
  shift_reminder: false,
};

export const volunteerMessageTemplateSchema = z
  .object({
    kind: volunteerMessageKindSchema,
    enabled: z.boolean(),
    /** Null er standardteksten (på mottakerens språk). */
    subject: z.string().nullable(),
    body: z.string().nullable(),
  })
  .meta({ id: 'VolunteerMessageTemplate' });
export type VolunteerMessageTemplate = z.infer<typeof volunteerMessageTemplateSchema>;

export const volunteerMessageTemplateListSchema = z
  .object({ items: z.array(volunteerMessageTemplateSchema) })
  .meta({ id: 'VolunteerMessageTemplateList' });
export type VolunteerMessageTemplateList = z.infer<typeof volunteerMessageTemplateListSchema>;

/** `PUT /v1/editions/:editionId/volunteer-messages/:kind`. */
export const updateVolunteerMessageTemplateRequestSchema = z
  .object({
    enabled: z.boolean(),
    subject: z
      .string()
      .trim()
      .max(200)
      .nullable()
      .transform((value) => (value === null || value === '' ? null : value)),
    body: z
      .string()
      .trim()
      .max(5000)
      .nullable()
      .transform((value) => (value === null || value === '' ? null : value)),
  })
  .strict()
  .meta({ id: 'UpdateVolunteerMessageTemplateRequest' });
export type UpdateVolunteerMessageTemplateRequest = z.infer<
  typeof updateVolunteerMessageTemplateRequestSchema
>;

/** `POST /v1/volunteers/:id/personal-link[/rotate|/send]` — lenken organisasjonen kan kopiere eller sende. */
export const personalLinkResponseSchema = z
  .object({
    url: z.string(),
    /** Om e-posten med lenken ble lagt i køen (`/send`). */
    sent: z.boolean(),
  })
  .meta({ id: 'PersonalLinkResponse' });
export type PersonalLinkResponse = z.infer<typeof personalLinkResponseSchema>;
